import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { LocalFaxService } from '../local-fax.service';
import { NotificationService } from '../../notification/notification.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaxService as ApiFaxService } from '../../api/services/fax.service';
import { phoneNumberValidator } from '../../core/validators/phone-number.validator';
import { FaxTemplateModel } from '../../api/models/fax-template-model';
import { UploadFaxComponent } from '../../editors/upload-fax/upload-fax.component';
import { CurrentUser } from '../../api/models/current-user';
import { ModulesEnum } from '../../core/security/modules.enum';
import { ContactsDialogComponent } from '../../dialogs/contacts/contacts-dialog.component';
import { ContactLookupModel } from '../../api/models/contact-lookup-model';
import { ZendeskService } from '../../core/services/zendesk.service';
import { LookupModel } from '../../api/models/lookup-model';
import { MatDialog } from '@angular/material/dialog';
import { FileHelpers } from '../../core/utilities/file.helpers';
import { NgIf, NgFor } from '@angular/common';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { MessagesModule } from 'primeng/messages';
import { MatSimpleSelectComponent } from '../../editors/mat-simple-select/mat-simple-select.component';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { FlexModule } from '@angular/flex-layout';
import { MatRecipientsMultiselectComponent } from '../../editors/mat-recipients-multiselect/mat-recipients-multiselect.component';
import { MatTextareaInputComponent } from '../../editors/mat-textarea-input/mat-textarea-input.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { FaxTemplateComponent } from '../fax-template/fax-template.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-send-fax',
  templateUrl: './send-fax.component.html',
  styleUrls: ['./send-fax.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatCard,
    MatCardContent,
    MessagesModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatSimpleSelectComponent,
    MatTextInputComponent,
    FlexModule,
    MatRecipientsMultiselectComponent,
    MatTextareaInputComponent,
    MatCheckboxInputComponent,
    MatFormFieldFix,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    UploadFaxComponent,
    FaxTemplateComponent,
    MatButton,
    MatCardActions,
    MatTooltip,
    Angular2PromiseButtonModule,
    Message,
  ],
})
export class SendFaxComponent extends BaseComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  contactsControl = new UntypedFormControl([], [Validators.required]);
  faxInternalNotesControl = new UntypedFormControl('', []);
  submitted: boolean;
  serverError: string;
  lastAdditionalAttachmentId = 0;
  additionalAttachments: any[] = [];

  faxNumbers: LookupModel[];
  coverPageTemplates: FaxTemplateModel[] = [];
  defaultCoverPageOption: FaxTemplateModel = {
    name: 'Select cover page',
    id: '-99',
    formFields: undefined,
    pdfTemplate: undefined,
    previewImage: undefined,
  };
  selectedCoverPageTemplate: FaxTemplateModel;
  coverPageParentData: any = {};
  useCoverPage = false;
  hasContactsModule = false;
  hasContactsViewRight = false;
  hasPhoneNumbersEditRight = false;

  file1: File | Blob;
  file2: File;

  allowMergeFaxes = true;

  userRights = {
    previewFax: false,
    downloadFax: false,
    deleteFax: false,
  };

  @ViewChild('fileInput1') fileInput1: UploadFaxComponent;
  @ViewChild('fileInput2', { static: true }) fileInput2: UploadFaxComponent;

  buttonSubmitPromise: Promise<void>;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    public authService: AuthService,
    public faxService: LocalFaxService,
    public zendeskService: ZendeskService,
    public dialog: MatDialog,
    private apiFaxService: ApiFaxService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.Fax_Edit, RolesEnum.Fax_Delete];
    this.deleteRoles = [RolesEnum.Fax_Delete];

    this.createForm();
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      this.hasContactsModule = this.authService.userHasPackage([
        ModulesEnum.ContactModule,
        ModulesEnum.PrivateContactModule,
      ]);
      this.hasContactsViewRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_View,
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_View,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);
      this.hasPhoneNumbersEditRight = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
      ]);
    } else {
      this.hasContactsModule = false;
      this.hasContactsViewRight = false;
      this.hasPhoneNumbersEditRight = false;
    }
  }

  createForm(): void {
    this.form = this.fb.group({
      numbersTo: this.contactsControl,
      numberFrom: ['', [phoneNumberValidator, Validators.required]],
      useCoverPage: [false],
      fromName: [''],
      faxInternalNotes: this.faxInternalNotesControl,
    });

    this.form.valueChanges.subscribe(x => {
      console.log('on form valueChanges');
      this.onFormChanged(x);
    });
  }

  resetForm() {
    console.log('resetForm');
    const currentUser = this.authService.currentUser.getValue();
    const defaultNumber = this.faxNumbers ? this.faxNumbers[0] : null;

    if (this.fileInput1) {
      this.fileInput1.removeAttachment();
    }

    if (this.fileInput2) {
      this.fileInput2.removeAttachment();
    }

    this.additionalAttachments = [];

    this.form.reset({
      useCoverPage: false,
      numbersTo: [],
      numberFrom: defaultNumber ? defaultNumber.id : '',
      fromName: currentUser.displayName,
      faxInternalNotes: '',
    });
  }

  async onSubmit() {
    console.log('onSubmit');
    this.serverError = '';

    if (this.form.valid && this.hasEditAccess) {
      if (!this.file1 && !this.file2 && this.additionalAttachments.filter(x => x.file).length === 0) {
        console.log('None files are selected');
        this.notificationService.notifyDanger('Please attach at least one attachment');
      } else {
        console.log('Start processing submit');
        this.buttonSubmitPromise = this.processSubmit();
      }
    } else if (!this.form.valid) {
      console.log('form is not valid', this.form.errors);
      for (const i of Object.keys(this.form.controls)) {
        this.form.controls[i].markAsDirty();
      }
    }
  }

  async processSubmit(): Promise<void> {
    this.submitted = true;
    const reqData = this.prepareFormModel();
    console.log(reqData);
    try {
      const { success, errorMessage } = await this.faxService.sendFax(reqData);

      if (success) {
        this.resetForm();
        this.notificationService.notifySuccess('Fax submitted successfully');
        await this.headerService.goToComponent('FaxListComponent');
      } else {
        this.serverError = errorMessage;
      }
    } catch (e: any) {
      this.serverError = e?.message;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): any {
    const formModel = this.form.getRawValue();
    const numbersTo = formModel.numbersTo.map(x => x.phoneNumber);

    const files = [];
    if (this.file1) {
      files.push(this.file1);
    }
    if (this.file2) {
      files.push(this.file2);
    }

    this.additionalAttachments.forEach(item => {
      if (item.file) {
        files.push(item.file);
      }
    });

    return {
      // numberTo: formModel.numberTo,
      numbersTo: numbersTo,
      numberFrom: formModel.numberFrom,
      fromName: formModel.fromName,
      files: files,
      canMerge: this.allowMergeFaxes,
      faxInternalNotes: formModel.faxInternalNotes,
      /*file1: this.file1,
      file2: this.file2*/
    };
  }

  preFillCoverPageParentData() {
    console.log('preFillCoverPageParentData()');
    const formData = this.prepareFormModel();
    this.coverPageParentData = {
      sendFromName: formData.fromName,
      sendFromPhoneNumber: formData.numberFrom,
      sendToPhoneNumber: formData.numbersTo[0] || '',
    };
  }

  onUseCoverPageChange(value: any) {
    console.log('onUseCoverPageChange', value);
    this.useCoverPage = value;
    if (!value) {
      this.selectedCoverPageTemplate = this.defaultCoverPageOption;
      this.file1 = undefined;
    }
  }

  onFaxTemplateChange($event: any) {
    console.log('onFaxTemplateChange', $event);
    this.selectedCoverPageTemplate = $event;
    this.file1 = undefined;
  }

  async ngOnInit() {
    console.log('ngOnInit');
    this.headerService.showBackButton(true);
    this.onContactSelected = this.onContactSelected.bind(this);
    this.faxNumbers = await this.faxService.faxOutgoingNumbersList();
    console.log(this.faxNumbers);

    this.faxService.getFaxTemplates().then(data => {
      this.coverPageTemplates = data;
    });

    this.resetForm();

    await super.ngOnInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.headerService.showBackButton(false);
  }

  onFileValidationError($event: string) {
    this.notificationService.notifyDanger($event);
  }

  onFileChanged1($event: File | Blob) {
    console.log('On file 1 changed', $event);
    this.file1 = $event;
    this.checkIfMergeFaxAllowed();
  }

  onFileChanged2($event: File) {
    console.log('On file 2 changed', $event);
    this.file2 = $event;
    this.checkIfMergeFaxAllowed();
  }

  checkIfMergeFaxAllowed() {
    let allowMerge = true;
    if (this.file1) {
      if (!FileHelpers.isSupportedFile(this.file1 as File, 'fax_merge')) {
        allowMerge = false;
      }
    }

    if (this.file2) {
      if (!FileHelpers.isSupportedFile(this.file2 as File, 'fax_merge')) {
        allowMerge = false;
      }
    }

    if (!allowMerge) {
      this.additionalAttachments = [];
    }

    this.allowMergeFaxes = allowMerge;
  }

  onFormChanged($event: Event) {
    this.preFillCoverPageParentData();
  }

  onOpenContactsList($event: MouseEvent) {
    const dialogRef = this.dialog.open(ContactsDialogComponent, {
      width: '70%',
      height: '90%',
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        console.log('after close', result);
        this.form.patchValue({
          numberTo: result,
        });
      }
    });
  }

  async onContactSelected(contacts: ContactLookupModel[]): Promise<void> {
    console.log(contacts);

    if (contacts.length > 0) {
      const contact = contacts[0];

      this.form.patchValue({
        numberTo: contact.phoneNumber,
      });
    }

    return;
  }

  openSupportWidget() {
    this.zendeskService.toggle();
  }

  addAdditionalAttachment() {
    this.additionalAttachments.push({
      id: this.lastAdditionalAttachmentId++,
      file: undefined,
    });
  }

  onAdditionalAttachmentChanged($event: File, attachment: any) {
    console.log('onAdditionalAttachmentChanged', $event);
    attachment.file = $event;

    if (!$event) {
      this.additionalAttachments = this.additionalAttachments.filter(x => x.id !== attachment.id);
    }
  }
}
